import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { WhatsappLogo, CaretLeft, PaperPlaneTilt, GearSix, CheckCircle, XCircle } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const inputCls = "h-10 bg-transparent border-white/10 text-white text-sm placeholder:text-white/25";
const lbl = "text-[10px] tracking-[0.15em] uppercase text-white/50";

export default function MarketingWhatsAppPage() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ provider: "twilio", account_sid: "", auth_token: "", from_number: "", meta_token: "", phone_number_id: "" });
  const [savingCfg, setSavingCfg] = useState(false);
  const [lists, setLists] = useState([]);
  const [log, setLog] = useState([]);
  const [send, setSend] = useState({ list_id: "", numbers: "", message: "" });
  const [sending, setSending] = useState(false);

  const loadCfg = () => api.get("/admin/whatsapp/config").then(({ data }) => {
    setCfg(data);
    setForm((f) => ({ ...f, provider: data.provider || "twilio", account_sid: data.account_sid || "", from_number: data.from_number || "", phone_number_id: data.phone_number_id || "" }));
  }).catch(() => {});
  const loadLog = () => api.get("/admin/whatsapp/log").then(({ data }) => setLog(data)).catch(() => {});

  useEffect(() => {
    loadCfg();
    loadLog();
    api.get("/admin/marketing/lists").then(({ data }) => setLists(data)).catch(() => {});
  }, []);

  const handleSaveCfg = async (e) => {
    e.preventDefault();
    setSavingCfg(true);
    try {
      const { data } = await api.put("/admin/whatsapp/config", form);
      toast.success(data.configured ? "Provider WhatsApp collegato!" : "Configurazione salvata (mancano ancora dei campi)");
      setForm((f) => ({ ...f, auth_token: "", meta_token: "" }));
      loadCfg();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingCfg(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/admin/whatsapp/send", {
        list_id: send.list_id || "",
        numbers: send.numbers.split(/[,;\n]/).map((n) => n.trim()).filter(Boolean),
        message: send.message,
      });
      if (data.ok > 0) toast.success(data.message, { duration: 8000 });
      else toast.error(`Nessun messaggio consegnato: ${data.failed?.[0]?.error || "errore provider"}`, { duration: 9000 });
      loadLog();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail), { duration: 9000 });
    } finally {
      setSending(false);
    }
  };

  const isTwilio = form.provider === "twilio";

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="marketing-whatsapp-page">
      <div className="mb-10 animate-fade-up">
        <Link to="/marketing" data-testid="back-to-marketing" className="inline-flex items-center gap-1 text-xs font-semibold text-white/50 hover:text-white transition-colors mb-4">
          <CaretLeft size={13} /> Marketing
        </Link>
        <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2 flex items-center gap-1.5">
          <WhatsappLogo size={13} className="text-emerald-400" /> Canale WhatsApp
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none flex flex-wrap items-center gap-4">
          WhatsApp
          {cfg && (cfg.configured ? (
            <span data-testid="whatsapp-status-badge" className="inline-flex items-center h-7 px-3.5 rounded-full bg-emerald-400/10 border border-emerald-400/30 text-[11px] font-semibold uppercase tracking-wider text-emerald-400">Attivo</span>
          ) : (
            <span data-testid="whatsapp-status-badge" className="inline-flex items-center h-7 px-3.5 rounded-full bg-amber-400/10 border border-amber-400/30 text-[11px] font-semibold uppercase tracking-wider text-amber-300">Non ancora attivo</span>
          ))}
        </h1>
        <p className="text-sm text-slate-400 mt-3 max-w-2xl">Invia messaggi WhatsApp ai numeri delle tue liste. Serve un account Twilio (con numero WhatsApp) oppure Meta WhatsApp Business Cloud API.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="p-8 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid="whatsapp-config-card">
          <GearSix size={32} weight="duotone" className="text-emerald-400 mb-3" />
          <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Provider</h2>
          <p className="text-sm text-slate-400 mb-5">Scegli e collega il provider di invio. Le chiavi vengono salvate cifrate.</p>
          <form onSubmit={handleSaveCfg} className="space-y-3">
            <div className="space-y-1.5">
              <Label className={lbl}>Provider</Label>
              <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                <SelectTrigger data-testid="whatsapp-provider-select" className="h-10 bg-transparent border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-white/10 text-white">
                  <SelectItem value="twilio" data-testid="provider-option-twilio">Twilio WhatsApp</SelectItem>
                  <SelectItem value="meta" data-testid="provider-option-meta">Meta WhatsApp Business (Cloud API)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isTwilio ? (
              <>
                <div className="space-y-1.5"><Label className={lbl}>Account SID</Label>
                  <Input data-testid="wa-account-sid-input" value={form.account_sid} onChange={(e) => setForm({ ...form, account_sid: e.target.value })} placeholder="ACxxxxxxxx..." className={inputCls} /></div>
                <div className="space-y-1.5"><Label className={lbl}>Auth Token {cfg?.has_auth_token && <span className="text-emerald-400 normal-case tracking-normal">(già salvato — lascia vuoto per non cambiarlo)</span>}</Label>
                  <Input data-testid="wa-auth-token-input" type="password" value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} placeholder="••••••••" className={inputCls} /></div>
                <div className="space-y-1.5"><Label className={lbl}>Numero WhatsApp Twilio</Label>
                  <Input data-testid="wa-from-number-input" value={form.from_number} onChange={(e) => setForm({ ...form, from_number: e.target.value })} placeholder="+14155238886" className={inputCls} /></div>
              </>
            ) : (
              <>
                <div className="space-y-1.5"><Label className={lbl}>Token permanente {cfg?.has_meta_token && <span className="text-emerald-400 normal-case tracking-normal">(già salvato)</span>}</Label>
                  <Input data-testid="wa-meta-token-input" type="password" value={form.meta_token} onChange={(e) => setForm({ ...form, meta_token: e.target.value })} placeholder="EAAG..." className={inputCls} /></div>
                <div className="space-y-1.5"><Label className={lbl}>Phone Number ID</Label>
                  <Input data-testid="wa-phone-number-id-input" value={form.phone_number_id} onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })} placeholder="106540352242922" className={inputCls} /></div>
              </>
            )}
            <Button type="submit" disabled={savingCfg} data-testid="whatsapp-save-config-button"
              className="h-10 px-5 bg-white text-black hover:bg-gray-200 rounded-full text-xs font-semibold">
              {savingCfg ? "Salvataggio..." : "Salva configurazione"}
            </Button>
          </form>
        </div>

        <div className="p-8 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid="whatsapp-send-card" style={{ animationDelay: "80ms" }}>
          <WhatsappLogo size={32} weight="duotone" className="text-emerald-400 mb-3" />
          <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Invia messaggio</h2>
          <p className="text-sm text-slate-400 mb-5">Scegli una lista (con numeri WhatsApp) e/o inserisci numeri manualmente in formato internazionale.</p>
          <form onSubmit={handleSend} className="space-y-3">
            <div className="space-y-1.5">
              <Label className={lbl}>Lista (opzionale)</Label>
              <Select value={send.list_id || "none"} onValueChange={(v) => setSend({ ...send, list_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="wa-list-select" className="h-10 bg-transparent border-white/10 text-white text-sm"><SelectValue placeholder="Nessuna lista" /></SelectTrigger>
                <SelectContent className="bg-surface border-white/10 text-white">
                  <SelectItem value="none">Nessuna lista</SelectItem>
                  {lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} ({(l.phones || []).length} numeri)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className={lbl}>Numeri aggiuntivi (separati da virgola)</Label>
              <Input data-testid="wa-numbers-input" value={send.numbers} onChange={(e) => setSend({ ...send, numbers: e.target.value })} placeholder="+393331234567, +393407654321" className={inputCls} />
            </div>
            <div className="space-y-1.5">
              <Label className={lbl}>Messaggio</Label>
              <Textarea required data-testid="wa-message-input" value={send.message} onChange={(e) => setSend({ ...send, message: e.target.value })} rows={4}
                placeholder="Ciao! Nuova promozione da Michel Chef..." className="bg-transparent border-white/10 text-white text-sm placeholder:text-white/25 resize-y" />
            </div>
            <Button type="submit" disabled={sending || !send.message.trim() || (!send.list_id && !send.numbers.trim())} data-testid="whatsapp-send-button"
              className="h-11 px-6 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm disabled:opacity-40">
              <PaperPlaneTilt size={15} className="mr-1.5" /> {sending ? "Invio in corso..." : "Invia WhatsApp"}
            </Button>
            {!cfg?.configured && <p className="text-[11px] text-amber-300/80">Configura prima il provider qui a fianco per attivare l'invio.</p>}
          </form>
        </div>
      </div>

      {log.length > 0 && (
        <div className="mt-8 border border-surface-border rounded-lg bg-surface p-6 animate-fade-up" data-testid="whatsapp-log-card">
          <h3 className="font-heading text-lg font-semibold tracking-tight mb-4">Storico invii WhatsApp</h3>
          <div className="space-y-2">
            {log.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs border-b border-white/5 pb-2">
                {r.ok === r.total ? (
                  <span className="flex items-center gap-1 text-emerald-400 shrink-0"><CheckCircle size={13} weight="fill" /> {r.ok}/{r.total}</span>
                ) : (
                  <span className="flex items-center gap-1 text-amber-300 shrink-0"><XCircle size={13} weight="fill" /> {r.ok}/{r.total}</span>
                )}
                <span className="text-white/40 shrink-0">{new Date(r.at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</span>
                <span className="text-white/35 uppercase text-[10px] tracking-wider shrink-0">{r.provider}</span>
                <span className="text-white/60 truncate">"{r.message}"</span>
                {r.failed?.length > 0 && <span className="text-red-400/80 truncate max-w-[280px]" title={r.failed[0].error}>{r.failed[0].number}: {r.failed[0].error}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </main>
  );
}
